import clsx from 'clsx';
import React, { memo, useEffect } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { dispatch, useReframe } from 'nike-re-framejs';

import useArrows from '@/_hooks/useArrows';
import BannersSelectionRow from '@/betsbanners/BannersSelectionRow';
import BannersSelectionHeader from '@/betsbanners/BannersSelectionHeader';
import { generateStoryImageUrl, range } from '@/common/util';
import immutableMemo from '@/common/immutableMemo';
import { Slide } from '@/swipeable/Slide';
import IntersectionObserver from '@/ui/IntersectionObserver';
import FixedAspectRatio from '@/common/FixedAspectRatio';
import Wrapper from './module/Wrapper/Wrapper';
import { trackBannerClick } from 'nike-ui/src/analytics/events/banner';
import { trackDwhBannerEvent } from 'nike-ui/src/components/dwh-tracking/api';
import LinkInterceptor from 'nike-ui/src/components/link-interceptor/LinkInterceptor';

function PaginationDot({ active }) {
    return <div className={clsx('dot', { active })} />;
}

export function Pagination({ size, index }) {
    const numberOfDots = size;
    if (numberOfDots < 2) return null;

    return (
        <div className="pagination">
            {range(0, numberOfDots).map(i => (
                <PaginationDot key={i} active={i === index} />
            ))}
        </div>
    );
}

// FIXME: Split into CasinoStories and BannerStories?
function Stories({ className }) {
    useEffect(() => dispatch(['homepage/stories/initialize']), []);
    const { derefSub } = useReframe();
    let stories = derefSub(['homepage/stories/data']);
    const { index, setIndex, leftDisabled, rightDisabled } = useArrows(0, stories.size - 1);
    if (!stories.size) return null;
    return (
        <Wrapper
            onLeftClick={() => setIndex(index - 1)}
            leftDisabled={leftDisabled}
            onRightClick={() => setIndex(index + 1)}
            rightDisabled={rightDisabled}
        >
            <div className={clsx('swipeable-views story-wrap', className)}>
                <SwipeableViews
                    containerStyle={{}}
                    className="swiper"
                    index={index}
                    resistance
                    onChangeIndex={index => setIndex(index)}
                >
                    {stories.map((story, index) => (
                        <Slide key={story.get('id')}>
                            <IntersectionObserver>
                                {visible => <Story story={story} visible={visible} index={index} />}
                            </IntersectionObserver>
                        </Slide>
                    ))}
                </SwipeableViews>
                <Pagination size={stories.size} index={index} />
            </div>
        </Wrapper>
    );
}

const Story = immutableMemo(function Story({ story, visible = true, index }) {
    const backgroundImage = generateStoryImageUrl(story.get('id'), 'story.image.link.url', visible);

    const betId = story.get('betId');
    const editorTip = story.get('betEditorTip');

    useEffect(() => {
        if (visible) trackStory('show');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const trackStory = eventName => {
        trackDwhBannerEvent(eventName, 'HOMEPAGE', 'IMG', story.get('id'), index, story.get('url'));
    };

    return (
        <LinkInterceptor
            href={story.get('url')}
            data-atid="mobile-story-item"
            data-name={story.get('title')}
            data-index={index}
            data-section="homepage"
            onClick={() => {
                trackStory('click');
                trackBannerClick({
                    type: 'IMG',
                    id: story.get('id'),
                    index: index,
                    location: '',
                    text: story.get('text'),
                    url: story.get('url'),
                    visibility: story.get('visibility')
                });
            }}
        >
            <h1 className="story-header">{story.get('title')}</h1>
            <h3 className="story-perex">
                <div className="shade shade-line-2">{story.get('text')}</div>
            </h3>
            <FixedAspectRatio className="story-image" style={{ backgroundImage }} width={1200} height={600}>
                {betId ? (
                    <div className="editor-tip-wrap" data-atid="hp-story-bets-box">
                        <BannersSelectionHeader betId={betId} />
                        <BannersSelectionRow betId={betId} editorTip={editorTip} />
                    </div>
                ) : null}
            </FixedAspectRatio>
        </LinkInterceptor>
    );
});

export default memo(Stories);
